if (!customElements.get("predictive-search")) {
  customElements.define(
    "predictive-search",
    class PredictiveSearch extends SearchForm {
      constructor() {
        super();
        this.cachedResults = {};
        this.predictiveSearchEl = this.querySelector(
          "[data-predictive-search]"
        );
        this.allPredictInstances =
          document.querySelectorAll("predictive-search");
        this.isOpen = false;
        this.abortController = new AbortController();
        this.searchTerm = "";

        this.setupEventListeners();
      }

      setupEventListeners() {
        if (!this.input) return;
        this.input.form?.addEventListener(
          "submit",
          this.onFormSubmit.bind(this)
        );

        this.input.addEventListener("focus", this.onFocus.bind(this));
        this.addEventListener("keyup", this.onKeyup.bind(this));
        //this.addEventListener("focusout", this.onFocusOut.bind(this));
      }

      getQuery() {
        return this.input.value.trim();
      }

      onChange() {
        const newSearchTerm = this.getQuery();

        if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
          // Remove the results when they are no longer relevant for the new search term
          // so they don't show up when the dropdown opens again
          this.querySelector("#predictive-search-results")?.remove();
        }

        // Update the term asap, don't wait for the predictive search query to finish loading
        this.updateSearchForTerm(this.searchTerm, newSearchTerm);

        this.searchTerm = newSearchTerm;

        if (!this.searchTerm.length) {
          this.close(true);
          if (this.predictiveSearchEl.closest(".section-search")) {
            this.getSearchResults(this.searchTerm);
          }
          return;
        }

        this.getSearchResults(this.searchTerm);
      }

      onFormSubmit(event) {
        if (
          !this.getQuery().length ||
          this.querySelector('[aria-selected="true"] a')
        ) {
          event.preventDefault();
        }
      }

      onFormReset(event) {
        event.preventDefault();
        // if on search page and predictive are closed, clean the URL and current search term
        if (
          this.closest(".section-search") &&
          !this.searchTerm &&
          !this.hasAttribute("open") &&
          window.location.search
        ) {
          const cleanURL = window.location.pathname;
          window.location.href = cleanURL;
          return;
        }

        this.searchTerm = "";
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);

        // if on search page and predictive are open, return value of query parameter to input
        if (this.closest(".section-search") && window.location.search) {
          const params = new URLSearchParams(window.location.search);
          const q = params.get("q");
          if (q) {
            this.input.value = q;
            this.resetBtn.style.display = "flex";
          } else {
            const cleanURL = window.location.pathname;
            window.location.href = cleanURL;
          }
        } else {
          this.input.focus();
        }
      }

      onFocus() {
        const currentSearchTerm = this.getQuery();
        if (!currentSearchTerm.length) return;

        if (this.searchTerm !== currentSearchTerm) {
          // Search term was changed from other search input, treat it as a user change
          this.onChange();
        } else if (this.getAttribute("results") === "true") {
          this.open();
        } else {
          this.getSearchResults(this.searchTerm);
        }
      }

      //onFocusOut() {
      //  if (!this.closest(".header-search-modal")) {
      //    setTimeout(() => {
      //      if (!this.contains(document.activeElement)) this.close(true);
      //    });
      //  }
      //}

      onKeyup(event) {
        if (!this.getQuery().length) this.close(true);
        event.preventDefault();
      }

      updateSearchForTerm(previousTerm, newTerm) {
        const searchForTextElement = this.querySelector(
          "[data-predictive-search-search-for-text]"
        );
        const currentText = searchForTextElement?.innerText;
        if (currentText) {
          if (currentText.matchAll(new RegExp(previousTerm, "g")).length > 1) {
            // The new term matches part of the text and not just the search term, do not replace to avoid mistakes
            return;
          }
          const newText = currentText.replace(previousTerm, newTerm);
          searchForTextElement.innerText = newText;
        }
      }

      getSearchResults(searchTerm) {
        const queryKey = searchTerm.replace(" ", "-").toLowerCase();
        this.setLiveRegionLoadingState();

        if (this.cachedResults[queryKey]) {
          this.renderSearchResults(this.cachedResults[queryKey]);
          return;
        }

        const baseUrl = routes.predictive_search_url;
        const qParam = `q=${encodeURIComponent(searchTerm)}`;
        const limitParam = `resources[limit]=6`;
        const scopeParam = `resources[limit_scope]=each`;
        const requestUrl = `${baseUrl}?${qParam}&${limitParam}&${scopeParam}&section_id=predictive-search`;

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) {
              var error = new Error(response.status);
              this.close();
              throw error;
            }
            return response.text();
          })
          .then((text) => {
            const resultsMarkup = new DOMParser()
              .parseFromString(text, "text/html")
              .querySelector("#shopify-section-predictive-search").innerHTML;
            // Save bandwidth keeping the cache in all instances synced
            this.allPredictInstances.forEach((predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            });
            this.renderSearchResults(resultsMarkup);
          })
          .catch((error) => {
            if (error?.code === 20) {
              // Code 20 means the call was aborted
              return;
            }
            this.close();
            throw error;
          });
      }

      setLiveRegionLoadingState() {
        this.statusElement =
          this.statusElement || this.querySelector(".predictive-search-status");
        this.loadingText =
          this.loadingText || this.getAttribute("data-loading-text");

        this.setLiveRegionText(this.loadingText);
        this.setAttribute("loading", true);
      }

      setLiveRegionText(statusText) {
        this.statusElement.setAttribute("aria-hidden", "false");
        this.statusElement.textContent = statusText;

        setTimeout(() => {
          this.statusElement.setAttribute("aria-hidden", "true");
        }, 1000);
      }

      renderSearchResults(resultsMarkup) {
        this.predictiveSearchEl.innerHTML = resultsMarkup;
        this.setAttribute("results", true);

        this.setLiveRegionResults();
        this.open();

        const parentModal = this.closest(".header-search-modal");
        if (parentModal) {
          trapFocus(parentModal, this.input);
        }
      }

      setLiveRegionResults() {
        this.removeAttribute("loading");
        this.setLiveRegionText(
          this.querySelector("[data-predictive-search-live-region-count-value]")
            .textContent
        );
      }

      open() {
        this.setAttribute("open", true);
        this.input.setAttribute("aria-expanded", true);
        this.isOpen = true;
      }

      close(clearSearchTerm = false) {
        this.closeResults(clearSearchTerm);
        this.isOpen = false;
      }

      closeResults(clearSearchTerm = false) {
        if (clearSearchTerm) {
          this.input.value = "";
          this.removeAttribute("results");
          if (this.resetBtn) this.resetBtn.removeAttribute("style");
        }
        const selected = this.querySelector('[aria-selected="true"]');

        if (selected) selected.setAttribute("aria-selected", false);

        this.input.setAttribute("aria-activedescendant", "");
        this.removeAttribute("loading");
        this.removeAttribute("open");
        this.input.setAttribute("aria-expanded", false);
        this.predictiveSearchEl.removeAttribute("style");
      }
    }
  );
}
